// Roles management (PX-73).
//
// Lets the admin create custom roles + edit permissions on existing
// ones. Built-in (`is_system=true`) rows show their permissions read-
// only — the backend refuses to mutate them. Deleting a role with
// active users surfaces a 409 with the user count, prompting the
// admin to reassign before retrying.

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { APIError, request } from '@/lib/api'
import type {
  PermissionCatalogResponse,
  Permission,
  RoleCreate,
  RoleResponse,
  RoleUpdate,
} from '@/types/api'

const SLUG_RE = /^[a-z0-9-]+$/

export function RolesPage() {
  const queryClient = useQueryClient()

  const rolesQuery = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => request<RoleResponse[]>('/admin/roles'),
  })
  const catalogQuery = useQuery({
    queryKey: ['admin', 'roles', 'permissions'],
    queryFn: () => request<PermissionCatalogResponse>('/admin/roles/permissions'),
    staleTime: 5 * 60_000,
  })

  const [editorState, setEditorState] = useState<
    | { mode: 'create' }
    | { mode: 'edit'; role: RoleResponse }
    | null
  >(null)

  return (
    <div className="p-6">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Roles</h1>
          <p className="text-sm text-ink-400">
            Create custom roles + tune permissions. Built-in roles are read-only.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditorState({ mode: 'create' })}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600"
        >
          + New role
        </button>
      </header>

      <div className="grid grid-cols-1 gap-3">
        {rolesQuery.isLoading && (
          <div className="rounded-xl border border-ink-100 bg-white p-4 text-center text-sm text-ink-400">Loading…</div>
        )}
        {rolesQuery.data?.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => setEditorState({ mode: 'edit', role: r })}
            className="flex items-center gap-4 rounded-xl border border-ink-100 bg-white p-4 text-left shadow-sm transition hover:border-brand-200 hover:shadow"
          >
            <RoleVisual role={r} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm font-medium text-ink-900">
                <span className="truncate">{r.name}</span>
                <span className="rounded bg-ink-50 px-1.5 py-0.5 font-mono text-[10px] text-ink-400">{r.slug}</span>
                {r.is_system && (
                  <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-200">
                    BUILT-IN
                  </span>
                )}
              </div>
              {r.description && (
                <div className="mt-0.5 text-xs text-ink-400">{r.description}</div>
              )}
              <div className="mt-1 flex items-center gap-3 text-xs text-ink-500">
                <span>{r.user_count} user{r.user_count === 1 ? '' : 's'}</span>
                <span>·</span>
                <span>{r.permissions.includes('*') ? 'all permissions' : `${r.permissions.length} permission${r.permissions.length === 1 ? '' : 's'}`}</span>
              </div>
            </div>
          </button>
        ))}
        {!rolesQuery.isLoading && rolesQuery.data?.length === 0 && (
          <div className="rounded-xl border border-ink-100 bg-white p-4 text-center text-sm text-ink-400">No roles yet.</div>
        )}
      </div>

      {editorState && (
        <RoleEditorModal
          mode={editorState.mode}
          initial={editorState.mode === 'edit' ? editorState.role : null}
          catalog={catalogQuery.data?.permissions ?? []}
          onClose={() => setEditorState(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] })
            setEditorState(null)
          }}
        />
      )}
    </div>
  )
}

function RoleVisual({ role }: { role: RoleResponse }) {
  const isAdmin = role.permissions.includes('*')
  const cls = isAdmin
    ? 'bg-brand-100 text-brand-700'
    : role.permissions.length > 0
    ? 'bg-amber-100 text-amber-800'
    : 'bg-ink-100 text-ink-500'
  const initial = role.name[0]?.toUpperCase() ?? '?'
  return (
    <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-lg font-semibold ${cls}`}>
      {initial}
    </div>
  )
}

// --- Editor modal --------------------------------------------------

interface EditorProps {
  mode: 'create' | 'edit'
  initial: RoleResponse | null
  catalog: Permission[]
  onClose: () => void
  onSaved: () => void
}

function RoleEditorModal({ mode, initial, catalog, onClose, onSaved }: EditorProps) {
  const isSystem = !!initial?.is_system
  const [slug, setSlug] = useState(initial?.slug ?? '')
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [permissions, setPermissions] = useState<Permission[]>(initial?.permissions ?? [])
  const [deleteConfirm, setDeleteConfirm] = useState(false)

  const slugInvalid = !!slug && !SLUG_RE.test(slug)
  const slugLocked = mode === 'edit'
  const canSave = !!name && !!slug && !slugInvalid

  const togglePerm = (p: Permission) => {
    setPermissions((prev) => {
      if (prev.includes(p)) return prev.filter((x) => x !== p)
      // Keep catalog ordering so the displayed list stays stable.
      const next = new Set([...prev, p])
      return catalog.filter((x) => next.has(x))
    })
  }

  const createMutation = useMutation({
    mutationFn: (body: RoleCreate) =>
      request<RoleResponse>('/admin/roles', { method: 'POST', body }),
    onSuccess: onSaved,
  })
  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: RoleUpdate }) =>
      request<RoleResponse>(`/admin/roles/${id}`, { method: 'PATCH', body }),
    onSuccess: onSaved,
  })
  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      request<void>(`/admin/roles/${id}`, { method: 'DELETE' }),
    onSuccess: onSaved,
  })

  const isPending = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending
  const error =
    createMutation.error instanceof APIError ? createMutation.error :
    updateMutation.error instanceof APIError ? updateMutation.error :
    deleteMutation.error instanceof APIError ? deleteMutation.error :
    null

  // 409 ROLE_IN_USE backend response carries context.count.
  const deleteCount = useMemo(() => {
    if (!(deleteMutation.error instanceof APIError)) return null
    if (deleteMutation.error.code !== 'ROLE_IN_USE') return null
    const ctx = deleteMutation.error.context
    if (ctx && typeof (ctx as Record<string, unknown>).count === 'number') {
      return (ctx as { count: number }).count
    }
    return null
  }, [deleteMutation.error])

  // Reset delete-confirm when error changes (so a 409 doesn't leave
  // the second "confirm" state armed).
  useEffect(() => {
    if (deleteMutation.error) setDeleteConfirm(false)
  }, [deleteMutation.error])

  const handleSave = () => {
    if (!canSave) return
    if (mode === 'create') {
      const body: RoleCreate = {
        slug,
        name,
        description: description.trim() || null,
        permissions,
      }
      createMutation.mutate(body)
    } else if (initial) {
      const body: RoleUpdate = {}
      if (name !== initial.name) body.name = name
      const trimmedDesc = description.trim() || null
      if (trimmedDesc !== initial.description) body.description = trimmedDesc
      if (!isSystem && JSON.stringify(permissions) !== JSON.stringify(initial.permissions)) {
        body.permissions = permissions
      }
      updateMutation.mutate({ id: initial.id, body })
    }
  }

  const handleDelete = () => {
    if (!initial) return
    if (!deleteConfirm) {
      setDeleteConfirm(true)
      return
    }
    deleteMutation.mutate(initial.id)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-6">
      <div className="my-4 w-full max-w-2xl rounded-xl bg-white shadow-xl">
        <header className="flex items-start justify-between gap-3 border-b border-ink-100 p-5">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              {mode === 'create' ? 'New role' : `Edit · ${initial?.name}`}
            </h2>
            {mode === 'edit' && (
              <p className="mt-0.5 font-mono text-[11px] text-ink-400">{initial?.slug}{isSystem ? ' · built-in' : ''}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ink-100 px-2 py-1 text-xs text-ink-500 transition hover:border-ink-200 hover:text-ink-900"
          >
            ✕ Close
          </button>
        </header>

        {error && (
          <div className="m-5 mb-0 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error.message}
            {deleteCount !== null && (
              <div className="mt-1 text-[11px] text-red-600">
                Reassign {deleteCount} user{deleteCount === 1 ? '' : 's'} first, then retry the delete.
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
          <Field label="Slug" required hint="lowercase, digits, dashes only">
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              disabled={slugLocked}
              className={`w-full rounded-md border px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 disabled:bg-ink-50 disabled:text-ink-400 ${
                slugInvalid
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                  : 'border-ink-200 focus:border-brand-500 focus:ring-brand-100'
              }`}
            />
            {slugInvalid && (
              <span className="mt-1 block text-[11px] text-red-600">Must match {String(SLUG_RE)}</span>
            )}
          </Field>

          <Field label="Name" required>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </Field>

          <Field label="Description" className="sm:col-span-2">
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </Field>

          <Field
            label={isSystem ? 'Permissions (built-in, read-only)' : 'Permissions'}
            className="sm:col-span-2"
            hint={isSystem ? 'Built-in roles cannot have their permissions edited.' : 'Pick the scopes this role can access.'}
          >
            <PermissionsGrid
              catalog={catalog}
              checked={permissions}
              onToggle={togglePerm}
              disabled={isSystem}
              wildcard={isSystem && (initial?.permissions ?? []).includes('*')}
            />
          </Field>
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-ink-100 p-5">
          <div>
            {mode === 'edit' && !isSystem && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={isPending}
                className={`rounded-md border px-3 py-1.5 text-sm font-semibold transition disabled:opacity-60 ${
                  deleteConfirm
                    ? 'border-red-500 bg-red-500 text-white hover:bg-red-600'
                    : 'border-red-300 text-red-600 hover:bg-red-50'
                }`}
              >
                {deleteConfirm ? 'Click again to confirm delete' : 'Delete'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave || isPending}
              className="rounded-md bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
            >
              {isPending ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}

function PermissionsGrid({
  catalog,
  checked,
  onToggle,
  disabled,
  wildcard,
}: {
  catalog: Permission[]
  checked: Permission[]
  onToggle: (p: Permission) => void
  disabled?: boolean
  wildcard?: boolean
}) {
  if (wildcard) {
    return (
      <div className="rounded-md border border-brand-200 bg-brand-50 p-3 text-xs font-medium text-brand-700">
        ✦ All permissions (`*` wildcard, admin only)
      </div>
    )
  }
  if (catalog.length === 0) {
    return <div className="text-xs text-ink-400">Permission catalog unavailable.</div>
  }
  // Group by the leading scope (`photoshoots.read` → group "photoshoots").
  const groups = new Map<string, Permission[]>()
  for (const p of catalog) {
    const scope = p.split('.')[0] ?? p
    const list = groups.get(scope) ?? []
    list.push(p)
    groups.set(scope, list)
  }
  return (
    <div className="grid grid-cols-2 gap-3">
      {[...groups.entries()].map(([scope, perms]) => (
        <div key={scope} className="rounded-md border border-ink-100 p-3">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-ink-400">{scope}</div>
          <div className="flex flex-col gap-1">
            {perms.map((p) => {
              const isChecked = checked.includes(p)
              const action = p.split('.').slice(1).join('.')
              return (
                <label
                  key={p}
                  className={`flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs ${disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-ink-50'}`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={disabled}
                    onChange={() => onToggle(p)}
                    className="h-3.5 w-3.5 rounded border-ink-300 text-brand-500 focus:ring-brand-200"
                  />
                  <span className="font-mono text-[11px] text-ink-700">{action}</span>
                </label>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

function Field({
  label,
  required,
  hint,
  className,
  children,
}: {
  label: string
  required?: boolean
  hint?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <label className={`block text-sm ${className ?? ''}`}>
      <span className="mb-1 flex items-center gap-1 text-xs font-medium text-ink-500">
        {label}
        {required && <span className="text-red-500">*</span>}
        {hint && <span className="ml-1 font-normal text-ink-400">— {hint}</span>}
      </span>
      {children}
    </label>
  )
}
